import { useEffect, useRef, useState, useCallback } from "react";
import "leaflet/dist/leaflet.css";
import "leaflet-draw/dist/leaflet.draw.css";
import L from "leaflet";
import "leaflet-draw";
import { api, formatApiErrorDetail } from "@/api";
import { confirmDialog } from "@/components/Confirm";
import { toast } from "sonner";
import { Hexagon, Trash2, Pencil, X, Crosshair } from "lucide-react";
import { MapShell } from "@/components/MapShell";
import { makeBaseLayer } from "@/lib/mapBase";

// Dedicated, easy-to-use editor for the service area (verzorgingsgebied).
// Draw one polygon that becomes the default focus/zoom area of every map.
export function ServiceAreaEditor({ canWrite }) {
  const mapEl = useRef(null);
  const mapRef = useRef(null);
  const drawnRef = useRef(null);
  const baseRef = useRef(null);
  const drawerRef = useRef(null);
  const [area, setArea] = useState(null);
  const [mapCfg, setMapCfg] = useState(null);
  const [buildings, setBuildings] = useState([]);
  const [ready, setReady] = useState(false);
  const [sat, setSat] = useState(false);
  const [drawing, setDrawing] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/settings/service-area"), api.get("/settings/map"), api.get("/buildings").catch(() => ({ data: [] }))])
      .then(([s, m, b]) => { setArea(s.data.polygon || null); setMapCfg(m.data); setBuildings(b.data || []); setReady(true); });
  }, []);

  const persistArea = useCallback(async (polygon) => {
    try { await api.put("/settings/service-area", { polygon }); toast.success("Verzorgingsgebied opgeslagen"); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  }, []);

  const fitArea = (map, poly) => { try { map.fitBounds(L.polygon(poly).getBounds().pad(0.12)); } catch (e) {} };
  const paintArea = (drawn, poly) => { drawn.clearLayers(); if (poly && poly.length >= 3) drawn.addLayer(L.polygon(poly, { color: "#2563eb", weight: 2, fillColor: "#2563eb", fillOpacity: 0.12 })); };

  useEffect(() => {
    if (!ready || !mapEl.current || mapRef.current) return;
    const center = buildings[0] && buildings[0].lat != null ? [buildings[0].lat, buildings[0].lng] : [52.11, 5.18];
    const map = L.map(mapEl.current, { zoomControl: true, attributionControl: false }).setView(center, 13);
    mapRef.current = map;
    baseRef.current = makeBaseLayer(mapCfg, false).addTo(map);
    buildings.forEach((b) => { if (b.lat != null) L.circleMarker([b.lat, b.lng], { radius: 4, color: "#1e40af", fillColor: "#3b82f6", fillOpacity: 1, weight: 1 }).addTo(map).bindPopup(b.name); });
    const drawn = new L.FeatureGroup(); map.addLayer(drawn); drawnRef.current = drawn;
    if (area && area.length >= 3) { paintArea(drawn, area); fitArea(map, area); }
    else if (buildings.length) { try { map.fitBounds(L.featureGroup(buildings.filter((b) => b.lat != null).map((b) => L.marker([b.lat, b.lng]))).getBounds().pad(0.3)); } catch (e) {} }
    map.on(L.Draw.Event.CREATED, (e) => {
      const c = e.layer.getLatLngs()[0].map((p) => [Number(p.lat.toFixed(6)), Number(p.lng.toFixed(6))]);
      paintArea(drawn, c); setArea(c); persistArea(c); fitArea(map, c); setDrawing(false);
    });
    setTimeout(() => map.invalidateSize(), 200);
  }, [ready]); // eslint-disable-line

  useEffect(() => {
    const map = mapRef.current; if (!map) return;
    if (baseRef.current) map.removeLayer(baseRef.current);
    baseRef.current = makeBaseLayer(mapCfg, sat).addTo(map);
    baseRef.current.bringToBack();
  }, [sat]); // eslint-disable-line

  const startDraw = () => {
    const map = mapRef.current; if (!map) return;
    drawnRef.current && drawnRef.current.clearLayers();
    setDrawing(true);
    const d = new L.Draw.Polygon(map, { allowIntersection: false, shapeOptions: { color: "#2563eb", weight: 2, fillOpacity: 0.1 } });
    drawerRef.current = d; d.enable();
  };
  const cancelDraw = () => {
    try { drawerRef.current && drawerRef.current.disable(); } catch (e) {}
    setDrawing(false);
    if (mapRef.current && drawnRef.current) paintArea(drawnRef.current, area);
  };
  const clearArea = async () => {
    if (!(await confirmDialog({ title: "Verzorgingsgebied wissen", message: "Weet je zeker dat je het verzorgingsgebied wilt wissen?", danger: true, confirmText: "Wissen" }))) return;
    drawnRef.current && drawnRef.current.clearLayers();
    setArea(null); await persistArea([]);
  };
  const recenter = () => { const map = mapRef.current; if (map && area && area.length >= 3) fitArea(map, area); };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="service-area-editor">
      <div className="mb-1 flex items-center gap-1.5 text-sm font-bold text-slate-900"><Hexagon className="h-4 w-4 text-blue-600" /> Verzorgingsgebied markeren</div>
      <p className="mb-3 text-xs text-slate-500">Teken het gebied waarin je korps werkt. Dit gebied is standaard waar elke kaart op inzoomt. Klik op <b>Teken gebied</b> en klik daarna hoekpunten op de kaart; dubbelklik of klik het eerste punt om af te sluiten.</p>

      {canWrite && (
        <div className="mb-3 flex flex-wrap items-center gap-2">
          {!drawing ? (
            <button data-testid="area-draw-btn" onClick={startDraw} className="inline-flex items-center gap-1.5 rounded-xl bg-blue-600 px-3.5 py-2 text-sm font-semibold text-white hover:bg-blue-500">
              <Pencil className="h-4 w-4" /> {area && area.length >= 3 ? "Opnieuw tekenen" : "Teken gebied"}
            </button>
          ) : (
            <button data-testid="area-cancel-btn" onClick={cancelDraw} className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3.5 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50">
              <X className="h-4 w-4" /> Annuleer tekenen
            </button>
          )}
          {area && area.length >= 3 && (
            <>
              <button data-testid="area-recenter-btn" onClick={recenter} className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50"><Crosshair className="h-4 w-4" /> Inzoomen op gebied</button>
              <button data-testid="area-clear-btn" onClick={clearArea} className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-red-600 hover:bg-red-50"><Trash2 className="h-4 w-4" /> Wissen</button>
            </>
          )}
          <span className="ml-auto text-xs font-semibold text-slate-500" data-testid="area-status">{area && area.length >= 3 ? `Ingesteld · ${area.length} hoekpunten` : "Nog niet ingesteld"}</span>
        </div>
      )}

      <MapShell heightClass="h-[460px]" testid="service-area-map" onToggle={() => setTimeout(() => mapRef.current && mapRef.current.invalidateSize(), 80)}>
        <button data-testid="area-satellite-toggle" onClick={() => setSat((s) => !s)}
          className={`absolute left-3 top-3 z-[1001] rounded-lg border px-3 py-1.5 text-xs font-semibold shadow ${sat ? "border-blue-500 bg-blue-600 text-white" : "border-slate-200 bg-white/95 text-slate-700 hover:bg-white"}`}>
          {sat ? "Satelliet aan" : "Satelliet"}
        </button>
        <div ref={mapEl} data-testid="service-area-map" className="h-full w-full overflow-hidden rounded-xl border border-slate-200" />
      </MapShell>
    </div>
  );
}
